import { action, computed, makeObservable, observable } from 'mobx';

import { devError, devLog } from '../../utils/log';
import type { RootStore } from '../RootStore';

export type WarmState = 'idle' | 'warming' | 'ready' | 'live';

/**
 * Owns one mini-games-ui iframe, kept booted so opening a minigame costs no boot.
 *
 * Its own iframe, never the slot or metagames one — sharing that is what let a metagame launch kill a
 * running slot session in PR #2644. `launch` returns false rather than throwing so callers keep the
 * MinigamesDialog fallback.
 */
export class MinigamesWarmStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }

  @observable
  public state: WarmState = 'idle';

  @observable
  public src: string | null = null;

  /** Bumped to force React to discard the iframe and mount a fresh one. */
  @observable
  public frameKey = 0;

  /** NOT observable: MobX deep-converts, and converting a cross-origin Window throws SecurityError. */
  private warmWindow: Window | null = null;

  @computed
  public get isVisible(): boolean {
    return this.state === 'live';
  }

  /**
   * Whether a launch posted right now would be taken.
   *
   * `startMinigame` opens the MinigamesDialog before it has a launch url, and that dialog renders an
   * iframe under a loader that only clears on a message from its own iframe. So the caller has to know
   * the answer before it decides to open the dialog, not after.
   */
  @computed
  public get canLaunch(): boolean {
    return this.state === 'ready' && !!this.warmWindow;
  }

  /**
   * Start booting a warm frame. Idempotent.
   *
   * `warm=1` keeps it inert: without it the app runs its auth bootstrap, which with no token in the url
   * throws and raises the error popup.
   */
  @action
  public warm = (): void => {
    if (this.state !== 'idle') {
      return;
    }

    this.src = `${import.meta.env.VITE_MINI_GAMES_URL}/?warm=1`;
    this.state = 'warming';
  };

  /**
   * mini-games-ui reported it is booted and inert.
   *
   * The window comes from this message, not the iframe's `onLoad`: those are two signals for one
   * condition and can disagree — onLoad can fire before the app's listener exists, dropping a launch.
   */
  @action
  public onWarmReady = (win?: Window | null): void => {
    if (win) {
      this.warmWindow = win;
    }

    if (this.state === 'warming') {
      this.state = 'ready';
    }
  };

  /** Posts a launch and reveals the frame. False means "use the dialog instead"; never throws. */
  @action
  public launch = (launchUrl: string): boolean => {
    // Refusing while live matters: a minigame accessToken has one gameId baked into the JWT, so a second
    // launch would carry a stale credential into another game — silently, not as an error.
    if (!this.canLaunch) {
      devLog(`MinigamesWarmFrame: falling back to the dialog — state=${this.state}`);

      return false;
    }

    try {
      this.warmWindow?.postMessage(
        { type: 'launchMinigame', payload: { launchUrl } },
        import.meta.env.VITE_MINI_GAMES_URL,
      );
    } catch (error) {
      devError('MinigamesWarmStore: posting the launch failed, falling back to the dialog', error);

      return false;
    }

    this.state = 'live';
    devLog('MinigamesWarmFrame: warm-launched — no boot on this open');

    return true;
  };

  /**
   * Hide the frame and hand it back to idle, keeping it booted, so the next open skips the boot.
   *
   * mini-games-ui returns to its inert warm route on goIdle, disposing the game's state and clearing
   * auth. Not destroyed: mobile and the slot iframe both keep one instance alive too.
   */
  @action
  public close = (): void => {
    // Each game's closeGame drives this and can fire twice per exit.
    if (this.state !== 'live') {
      return;
    }

    if (!this.warmWindow) {
      this.state = 'idle';

      return;
    }

    try {
      this.warmWindow.postMessage({ type: 'goIdle' }, import.meta.env.VITE_MINI_GAMES_URL);
    } catch (error) {
      devError('MinigamesWarmStore: goIdle failed, dropping the frame instead', error);
      // Unreachable document: do not claim it is reusable.
      this.warmWindow = null;
      this.state = 'idle';
      this.frameKey += 1;

      return;
    }

    this.state = 'ready';
  };
}
